import type { SxProps } from '@trmediaab/zebra';
import { Box } from '@trmediaab/zebra';
import { forwardRef } from 'react';

import { useImmutableState } from '../../state';
import { useLayoutContext } from '../../utils/useLayoutContext';

interface AreaProps extends React.ComponentProps<typeof Box>, SxProps {
  area: string;
  children: React.ReactNode;
}

interface LayoutProps extends React.ComponentProps<typeof Box>, SxProps {
  areas?: {
    oneCol: string[];
    twoCol?: string[];
  };
  children: React.ReactNode;
}

const toTemplateAreas = (areas: string[]) =>
  areas.map(area => `"${area}"`).join(' ');

const Layout = forwardRef<HTMLDivElement, LayoutProps>(
  ({ children, areas, sx, ...props }, ref) => {
    const layoutVariant = useLayoutContext();
    const { appMode } = useImmutableState();
    const showProgress = appMode !== 'redeemGift';

    let gridProps: SxProps['sx'] = {
      gridTemplateColumns: 'auto',
      gridGap: ['3,5', null, 4],
    };

    if (areas != null) {
      if (layoutVariant === 'wide' && areas.twoCol != null) {
        gridProps = {
          ...gridProps,
          gridTemplateColumns: ['auto', null, '1fr 1fr'],
          gridTemplateAreas: [
            toTemplateAreas(
              [showProgress && 'progress', ...areas.oneCol, 'nextstep'].filter(
                Boolean,
              ),
            ),
            null,
            toTemplateAreas(
              [
                showProgress && 'progress progress',
                ...areas.twoCol,
                'nextstep nextstep',
              ].filter(Boolean),
            ),
          ],
        };
      } else {
        gridProps = {
          ...gridProps,
          gridTemplateAreas: toTemplateAreas(
            [showProgress && 'progress', ...areas.oneCol, 'nextstep'].filter(
              Boolean,
            ),
          ),
        };
      }
    }

    if (sx != null) {
      gridProps = { ...gridProps, ...sx };
    }

    return (
      <Box display="grid" sx={gridProps} {...props} ref={ref}>
        {children}
      </Box>
    );
  },
);

const Area = ({ area, children, sx, ...props }: AreaProps) => (
  <Box width="100%" sx={{ gridArea: area, ...sx }} data-area={area} {...props}>
    {children}
  </Box>
);

const ProgressArea = ({ children, ...props }: Omit<AreaProps, 'area'>) => (
  <Area {...props} area="progress" mb="2">
    {children}
  </Area>
);

const NextStepArea = ({ children, ...props }: Omit<AreaProps, 'area'>) => (
  <Area {...props} area="nextstep" mt="3">
    {children}
  </Area>
);

export default Object.assign(Layout, {
  area: Area,
  progressArea: ProgressArea,
  nextStepArea: NextStepArea,
});
